import { memo, useCallback, useMemo, useState, useRef, useEffect, type MouseEvent } from 'react'
import { TableCell, TableRow, Typography, Stack, alpha, Box, useTheme } from '@mui/material'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { useDispatch, useSelector } from 'react-redux'
import { OptionType, SecurityCategory_CategoryCode, Security } from '../../proto/generated/security'
import { useStyles } from './styles'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import ThinArrowRightIcon from '../../assets/icons/ThinArrowRightIcon'
import { GroupedPosition } from '../../reducer/positions.reducer'
import ProfitCell from './profitCell'
import LtpCell from './ltpCell'
import BasketExpandedProfitRow from './basketExpandedProfitRow'
import { toLocaleStringIn } from '../../util/general'
import ActionMenu from './actionMenu'
import { selectGroupBy, selectPositions } from '../../selectors/positions'
import { formatGreeks } from '../../pages/home/optionChainViewTabs/components/DynamicColumnRender.tsx'
import { selectPositionGreeksSignature } from '../../selectors/security.ts'
import store from '../../store'
import { getPositionSecurityKeys } from '../../util/positionLtpsSignature'
import { filterBasketPositionChildrenHidingAuxiliaryOnlyLegs, getPositionProduct, getSquareOffOrder } from '../../util/positions'
import { exchangeToJSON, TimeBucket_TimeSeries } from '../../proto/generated/common.ts'
import { OrderStatus, OrderType, productToJSON, TransactionType, Broker_OrderType, Product, Order } from '../../proto/generated/order.ts'
import { selectOrdersHistory } from '../../selectors/orders.ts'
import SlTargetDialog from '../TradePanel/slAndTargetInputs/SlTargetDialog.tsx'
import { ORDERS_ON_CHART_YELLOW as yellow } from '../chartV2/drawingTools/ordersOnChartTool/ordersOnChartTool.constants'
import { ExtraSmallButton } from '../custom'
import ExitIcon from '../../assets/icons/ExitIcon'
import Chart from '../../assets/icons/Chart'
import TuneOutlinedIcon from '../../assets/icons/TuneOutlinedIcon'
import PlusIcon from '../../assets/icons/PlusIcon'
import { TradeOrder } from '../../reducer/strategyBuilder.reducer'
import { STRATEGY_BUILDER_LOCATION_PENDING_KEY, type StrategyBuilderLocationState } from '../../util/strategyBuilderFromPositionsNavigation'
import { generatePath, useNavigate } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import { selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { fetchOptionChainV2 } from '../../util/options'
import { GetOptionChainRequestV2 } from '../../proto/generated/candle-data-depot-option-chain'
import { setQuickOrderData } from '../../reducer/orders.reducer'
import { v4 as uuid } from 'uuid'
import {
    isPlaceholderStrategyGroupName,
    ORDER_TYPES_STRATEGY_AUXILIARY,
    resolveBasketBatchStrategyGroupName,
} from '../../util/orders'
import {
    batchHasAnyPrimaryLegFill,
    basketBookHasOpenExposureForBasket,
    batchHasNonTerminalConditionalExit,
    conditionalExitLegAllowsRuleEditInUi,
    findBasketConditionalExitLeg,
    resolveBatchIdForAnalyzeFromOrders,
    resolveBatchIdForBasketFromOrders,
} from '../../util/conditionalEntry'

import type { useStandalonePositionConditionalExit } from '../../hooks/useStandalonePositionConditionalExit'
import type { useBasketPositionConditionalExit } from '../../hooks/useBasketPositionConditionalExit'
import { useUser } from '../../context/UserContext'
import { FeatureCategory, FeatureName } from '../adminUserRestrictions/Restrictions.constants'
import { findStandaloneConditionalExitLeg, findStandaloneSquareOffEntryBatch } from '../../util/quickTradeConditionalPlace.ts'

type OpenConditionalExitModal = ReturnType<typeof useStandalonePositionConditionalExit>['openConditionalExitModal']
type OpenBasketConditionalExitModal = ReturnType<typeof useBasketPositionConditionalExit>['openBasketConditionalExitModal']

export type PositionsTableRowProps = {
    position: GroupedPosition
    expandEnabled?: boolean
    showActions?: boolean
    child?: boolean
    showGreeks?: boolean
    openConditionalExitModal: OpenConditionalExitModal
    openBasketConditionalExitModal: OpenBasketConditionalExitModal
}

const PositionsTableRow: React.FC<PositionsTableRowProps> = ({
    position,
    expandEnabled,
    child,
    showGreeks,
    openConditionalExitModal,
    openBasketConditionalExitModal,
}) => {
    const { classes, cx } = useStyles()
    const theme = useTheme()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { security: sec, quantity, avgPrice, groupId, children } = position
    const securityKey = useMemo(() => getSecurityKey(sec.exchange, sec.code), [sec])
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const [underlyingSecurity, setUnderlyingSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => {
        if (!securityKey) {
            setSecurity(undefined)
            setUnderlyingSecurity(undefined)
            return
        }
        getSecurityAsync(securityKey).then((s) => {
            setSecurity(s)
            if (s?.underlyingSecurityCode) {
                getSecurityAsync(getSecurityKey(s.exchange, s.underlyingSecurityCode)).then(setUnderlyingSecurity)
            } else {
                setUnderlyingSecurity(undefined)
            }
        })
    }, [securityKey])
    const groupBy = useSelector(selectGroupBy)
    const positionSecurityKeys = useMemo(() => getPositionSecurityKeys(position), [position])
    const greeksSignature = useSelector(selectPositionGreeksSignature(showGreeks ? positionSecurityKeys : []))
    const orderHistory = useSelector(selectOrdersHistory)
    const allPositions = useSelector(selectPositions)

    const effectiveChildren = useMemo(() => {
        if (!children?.length) return children
        if (groupBy !== 'basket' || !groupId || groupId.startsWith('default_')) return children
        return filterBasketPositionChildrenHidingAuxiliaryOnlyLegs(children, groupId, orderHistory)
    }, [groupBy, groupId, children, orderHistory])
    const {
        user: { hasAccess },
    } = useUser()
    const hasSlTargetFeature = useMemo(
        () => hasAccess(FeatureCategory.TRADING, '', FeatureName.SL_AND_TARGET),
        [hasAccess]
    )
    const positionIsStandaloneDefault = useMemo(
        () =>
            (position.children?.length === 1 && position.children[0].groupId?.startsWith('default')) ?? false,
        [position.children]
    )
    const standaloneCxLeg = useMemo(() => {
        if (!positionIsStandaloneDefault || !position.children?.[0]?.security) return undefined
        const ch = position.children[0]
        const sec = ch.security
        const entryBatch = findStandaloneSquareOffEntryBatch(orderHistory, position.groupId, sec)
        return findStandaloneConditionalExitLeg(orderHistory, position.groupId, sec, entryBatch)
    }, [positionIsStandaloneDefault, position.children, position.groupId, orderHistory])
    const standaloneConditionalExitMenuEnabled = useMemo(
        () =>
            !child &&
            positionIsStandaloneDefault &&
            hasSlTargetFeature &&
            position.quantity !== 0 &&
            (!standaloneCxLeg || conditionalExitLegAllowsRuleEditInUi(standaloneCxLeg)),
        [
            child,
            positionIsStandaloneDefault,
            hasSlTargetFeature,
            position.quantity,
            standaloneCxLeg,
        ]
    )
    const standaloneConditionalExitMenuLabel = useMemo(
        () => (standaloneCxLeg ? 'Edit conditional exit' : 'Add conditional exit'),
        [standaloneCxLeg]
    )
    const resolvedBatchForBasketCx = useMemo(
        () => resolveBatchIdForBasketFromOrders(orderHistory, position.groupId),
        [orderHistory, position.groupId]
    )
    const basketCxLeg = useMemo(() => {
        if (!position.groupId || position.groupId.startsWith('default_')) return undefined
        return findBasketConditionalExitLeg(orderHistory, position.groupId, resolvedBatchForBasketCx)
    }, [position.groupId, orderHistory, resolvedBatchForBasketCx])
    const basketPositionHasPrimaryFill = useMemo(
        () =>
            !!position.groupId &&
            !position.groupId.startsWith('default_') &&
            batchHasAnyPrimaryLegFill(
                orderHistory || [],
                position.groupId,
                resolvedBatchForBasketCx ?? undefined
            ),
        [position.groupId, orderHistory, resolvedBatchForBasketCx]
    )
    const basketHasLegsInBook = useMemo(
        () => basketBookHasOpenExposureForBasket(allPositions, position.groupId),
        [allPositions, position.groupId]
    )
    const isBasketStrategyParentRow = useMemo(
        () =>
            !child &&
            !!position.groupId &&
            !position.groupId.startsWith('default_') &&
            (effectiveChildren?.length ?? 0) >= 1,
        [child, position.groupId, effectiveChildren?.length]
    )
    /** Collapsed ⋮ menu: show Add/Edit conditional exit when order history shows fills or book still has basket legs. */
    const basketConditionalExitMenuEnabled = useMemo(
        () =>
            isBasketStrategyParentRow &&
            hasSlTargetFeature &&
            position.quantity !== 0 &&
            (basketPositionHasPrimaryFill || basketHasLegsInBook) &&
            (!basketCxLeg || conditionalExitLegAllowsRuleEditInUi(basketCxLeg)),
        [
            isBasketStrategyParentRow,
            hasSlTargetFeature,
            position.quantity,
            basketPositionHasPrimaryFill,
            basketHasLegsInBook,
            basketCxLeg,
        ]
    )
    const basketConditionalExitMenuLabel = useMemo(
        () => (basketCxLeg ? 'Edit conditional exit' : 'Add conditional exit'),
        [basketCxLeg]
    )
    const quantityClass = quantity > 0 ? classes.buyQuantity : quantity < 0 ? classes.sellQuantity : undefined
    const [expanded, setExpanded] = useState(false)
    const [openSlTarget, setOpenSlTarget] = useState(false)
    const [containerWidth, setContainerWidth] = useState(0)
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!containerRef.current) return

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width)
            }
        })

        resizeObserver.observe(containerRef.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [])

    const shouldWrapButtons = containerWidth > 0 && containerWidth < 400

    const basketExitHiddenByConditionalExit = useMemo(
        () =>
            groupBy === 'basket' &&
            !!groupId &&
            !groupId.startsWith('default_') &&
            batchHasNonTerminalConditionalExit(orderHistory, groupId, undefined),
        [groupBy, groupId, orderHistory]
    )

    const underlyingToken = getSecurityKey(security?.exchange, security?.underlyingSecurityCode)
    const optionsTradesData = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))

    const borderColor = useMemo(() => {
        if (!position.groupId || position.groupId.startsWith("default_")) return null
        return alpha(theme.palette.primary.main, 0.4)
    }, [position.groupId, theme.palette.primary.main])

    // Check if this is an incoming order (shown as a disabled child row)
    const isIncomingPosition = useMemo(() => {
        return (position as any).isIncoming === true
    }, [position])

    const isLastRow = useMemo(() => {
        return (position as any).isLastRow === true
    }, [position])

    const isLastChild = useMemo(() => {
        return (position as any).isLastChild === true
    }, [position])

    const toggleExpand = useCallback(() => {
        setExpanded(!expanded)
    }, [expanded])
    const handleSlTargetClick = useCallback(() => {
        setOpenSlTarget(true)
    }, [])
    const handleStandaloneConditionalExitFromMenu = useCallback(
        async (e: MouseEvent) => {
            e.stopPropagation()
            if (!position.children?.length) return
            const isStandalone =
                position.children.length === 1 && position.children[0].groupId?.startsWith('default')
            if (!isStandalone) return
            const standalonePosition = position.children[0]
            const securityKey = getSecurityKey(
                standalonePosition.security?.exchange,
                standalonePosition.security?.code
            )
            const standaloneSec = await getSecurityAsync(securityKey)
            if (!standaloneSec) return
            const finalProduct = standalonePosition.originalPosition?.product ?? Product.INTRADAY
            await openConditionalExitModal({
                groupId: position.groupId,
                security: standaloneSec,
                product: finalProduct,
                standaloneLeg: standalonePosition,
            })
        },
        [position.children, position.groupId, openConditionalExitModal]
    )
    const handleBasketConditionalExitFromMenu = useCallback(
        async (e: MouseEvent) => {
            e.stopPropagation()
            if (!isBasketStrategyParentRow || !effectiveChildren?.length || !position.groupId) return

            const resolveUnderlyingIndexForBasketCx = async (): Promise<Security | null> => {
                if (underlyingSecurity) return underlyingSecurity
                const ch = effectiveChildren.find((c) => c.security?.code)
                if (!ch?.security) return null
                const leg = await getSecurityAsync(
                    getSecurityKey(ch.security.exchange, ch.security.code)
                )
                if (!leg) return null
                if (leg.underlyingSecurityCode) {
                    return (
                        (await getSecurityAsync(
                            getSecurityKey(leg.exchange, leg.underlyingSecurityCode)
                        )) ?? null
                    )
                }
                return leg
            }

            const und = await resolveUnderlyingIndexForBasketCx()
            if (!und) return

            const batchId = resolveBatchIdForBasketFromOrders(orderHistory, position.groupId)
            const sgFromBook =
                batchId != null && batchId !== ''
                    ? resolveBasketBatchStrategyGroupName(orderHistory, position.groupId, batchId)
                    : undefined
            const rawName = position.groupName?.trim()
            const strategyGroupForCx =
                rawName && !isPlaceholderStrategyGroupName(rawName)
                    ? rawName
                    : sgFromBook && !isPlaceholderStrategyGroupName(sgFromBook)
                      ? sgFromBook
                      : `${und.symbol} Basket`

            await openBasketConditionalExitModal({
                groupId: position.groupId,
                underlyingSecurity: und,
                positionChildren: effectiveChildren,
                strategyGroup: strategyGroupForCx,
            })
        },
        [
            isBasketStrategyParentRow,
            effectiveChildren,
            position.groupId,
            position.groupName,
            underlyingSecurity,
            orderHistory,
            openBasketConditionalExitModal,
        ]
    )
    const basketOpenSlOrder = useMemo(() => {
        return (orderHistory || []).find((o) => {
            return o.basketId === position.groupId && [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status) && o.orderType === OrderType.STOP_LOSS
        })
    }, [position.groupId, orderHistory])
    const basketOpenTargetOrder = useMemo(() => {
        return (orderHistory || []).find((o) => {
            return o.basketId === position.groupId && [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status) && o.orderType === OrderType.TARGET
        })
    }, [position.groupId, orderHistory])
    const hasSL = !!basketOpenSlOrder
    const hasTarget = !!basketOpenTargetOrder
    const slTargetLabel = useMemo(() => {
        if (!hasSL && !hasTarget) return 'Add SL/TP'
        if (hasSL && !hasTarget) return 'Add Target'
        if (!hasSL && hasTarget) return 'Add Stop Loss'
        return 'Edit SL/TP'
    }, [hasSL, hasTarget])
    const positionGreeks = useMemo(() => {
        if (!showGreeks) return null

        const greeksData = store.getState().securityStore.greeks
        if (!greeksData) return null

        let delta = 0
        let theta = 0
        let gamma = 0
        let vega = 0

        const processPosition = (pos: GroupedPosition) => {
            if (pos.quantity === 0) return

            const securityKey = getSecurityKey(pos.security.exchange, pos.security.code)
            const optionGreeks = greeksData[securityKey]

            if (optionGreeks) {
                const qty = pos.quantity
                delta += (optionGreeks.delta ?? 0) * qty
                theta += (optionGreeks.theta ?? 0) * qty
                gamma += (optionGreeks.gamma ?? 0) * qty
                vega += (optionGreeks.vega ?? 0) * qty
            }
        }

        if (effectiveChildren && effectiveChildren.length > 1) {
            effectiveChildren.forEach(processPosition)
        } else {
            processPosition(position)
        }

        return { delta, theta, gamma, vega }
    }, [showGreeks, position, effectiveChildren, greeksSignature])

    const partialSquareOffLeg = useMemo(() => {
        if (!child || isIncomingPosition) return null

        const squareOffOrders = (orderHistory || []).filter((o) => {
            const isSameBasket = o.basketId === position.groupId
            const isExecuted = o.status === OrderStatus.EXECUTED
            const isOppositeDirection = position.quantity > 0 ? o.transactionType === TransactionType.SELL : o.transactionType === TransactionType.BUY
            const isSameSecurity = getSecurityKey(o.security.exchange, o.security.code) === getSecurityKey(position.security.exchange, position.security.code)
            return (
                isSameBasket &&
                isExecuted &&
                isOppositeDirection &&
                isSameSecurity && !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType)
            )
        })

        if (squareOffOrders.length === 0) return null

        const totalSquaredOff = squareOffOrders.reduce((sum, o) => sum + o.quantity, 0)
        if (position.quantity === 0 || totalSquaredOff === 0) return null

        return {
            squaredOffQuantity: position.quantity > 0 ? totalSquaredOff : -totalSquaredOff,
            avgPrice: position.avgPrice
        }
    }, [position, orderHistory, child])

    const incomingOrders = useMemo(() => {
        const rawIncomingOrders = (orderHistory || []).filter((o) => {
            return (effectiveChildren?.length ?? 0) && !position.groupId.startsWith("default_") && o.basketId === position.groupId && [OrderStatus.OPEN, OrderStatus.INACTIVE, OrderStatus.PARTIALLY_FILLED].includes(o.status) && !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType) && o.orderType !== OrderType.CONDITIONAL_ENTRY && o.orderType !== OrderType.CONDITIONAL_EXIT
        });

        // Group iceberg orders by security (basketId + batchId + security)
        const groupedOrders = new Map<string, Order[]>();
        const regularOrders: Order[] = [];

        rawIncomingOrders.forEach(order => {
            const hasIcebergParams = order.orderExecutionParams?.icebergNumberOfLegs && 
                                     order.orderExecutionParams?.icebergLegNumber;
            
            if (hasIcebergParams && order.batchId && order.basketId) {
                // This is an iceberg leg order
                const securityCode = order.security?.code ?? '';
                const securityExchange = order.security?.exchange ?? 0;
                
                // Group by basketId + batchId + securityCode + exchange
                const groupKey = `${order.basketId}_${order.batchId}_${securityExchange}_${securityCode}`;
                
                if (!groupedOrders.has(groupKey)) {
                    groupedOrders.set(groupKey, []);
                }
                groupedOrders.get(groupKey)!.push(order);
            } else {
                // Regular order
                regularOrders.push(order);
            }
        });

        // Create combined orders for iceberg groups
        const combinedOrders: Order[] = [];
        
        groupedOrders.forEach((orders) => {
            if (orders.length === 0) return;
            
            // Sort by leg number
            orders.sort((a, b) => 
                (a.orderExecutionParams?.icebergLegNumber ?? 0) - 
                (b.orderExecutionParams?.icebergLegNumber ?? 0)
            );

            // Create a combined order representing all legs of this security
            const firstOrder = orders[0];
            const totalQuantity = orders.reduce((sum, o) => sum + o.quantity, 0);
            const totalFilledQuantity = orders.reduce((sum, o) => sum + o.filledQuantity, 0);
            
            // Use the first order as the base and update quantities
            const combinedOrder: Order = {
                ...firstOrder,
                quantity: totalQuantity,
                filledQuantity: totalFilledQuantity,
                // Add a flag to indicate this is a combined iceberg order
                clientOrderId: `combined_iceberg_${firstOrder.basketId}_${firstOrder.batchId}_${firstOrder.security?.code}`
            };
            
            combinedOrders.push(combinedOrder);
        });

        // Return combined iceberg orders + regular orders
        return [...combinedOrders, ...regularOrders];
    }, [position.groupId, orderHistory, effectiveChildren?.length])

    const displayName = useMemo(() => {
        // Only show basket name if it's actually a basket and not a default group
        if (groupBy === 'basket' && !position?.groupId.startsWith("default_") && (effectiveChildren?.length ?? 0) >= 1) {
            return position.groupName
        }

        const sec = security
        let result = (sec?.categoryCode === SecurityCategory_CategoryCode.OPTIONS ? underlyingSecurity?.symbol : null) || sec?.symbol
        if (sec?.categoryCode === SecurityCategory_CategoryCode.OPTIONS) {
            if (position?.groupId.startsWith("default_")) {
                result = underlyingSecurity?.symbol || result
            } else {
                result = ''
            }
            const expiry = new Date(sec?.expiry?.expiry ?? 0)
            const day = expiry.getDate()
            const month = expiry.toLocaleString('en', { month: 'short' }).toUpperCase()
            const year = expiry.getFullYear()
            const currentYear = new Date().getFullYear()
            if (year === currentYear) {
                result += ` ${day} ${month}`
            } else {
                result += ` ${day} ${month} ${year.toString().slice(-2)}`
            }
            result += ` ${(sec.strikePrice ?? 0) / (sec.priceMultiplier ?? 100)} ${sec.optionType === OptionType.CE ? 'CE' : 'PE'}`
        }
        return result
    }, [security, underlyingSecurity, groupId, groupBy, position, effectiveChildren])

    return (
        <>
            <TableRow>
                {
                    !child ?
                    <TableCell className={cx(classes.tableCell, classes.symbolCell)} colSpan={5} sx={{ paddingY: '0px !important', position: 'relative', height: '6px' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: '5px',
                                height: '5px',
                                backgroundColor: 'none'
                            }}
                        />
                    </TableCell> : null
                }
            </TableRow>
            <TableRow
                onClick={(isIncomingPosition || position.groupId.startsWith("default_")) ? undefined : toggleExpand}
                className={cx(
                    classes.tableRow,
                    position.quantity === 0 ? classes.closedPositionRow : undefined,
                    isIncomingPosition ? classes.closedPositionRow : undefined
                )}
                sx={{
                    ...(child && borderColor && {
                        background: 'transparent !important'
                    }),
                    ...(child && {
                        height: '36px !important'
                    }),
                    position: 'relative'
                }}
            >
                <TableCell className={cx(classes.tableCell, classes.symbolCell)} sx={{ position: 'relative', zIndex: 1, opacity: '1 !important', overflow: 'visible !important' }}>
                    {borderColor && child && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: '5px',
                                backgroundColor: borderColor,
                                ...((isLastRow || isLastChild) && {
                                    borderBottomLeftRadius: '8px'
                                })
                            }}
                        />
                    )}
                    {expanded && borderColor && !child && (
                        <>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '5px',
                                    backgroundColor: borderColor,
                                    borderTopLeftRadius: '8px'
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: '5px',
                                    top: 0,
                                    width: 'calc(100% - 5px)',
                                    height: '1px',
                                    backgroundColor: borderColor,
                                    zIndex: 1
                                }}
                            />
                        </>
                    )}
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%', minWidth: 0, marginLeft: child ? '16px' : '4px', opacity: (position.quantity === 0 || isIncomingPosition) ? 0.5 : 1 }}>
                        <Typography
                            variant="body1"
                            title={displayName}
                            sx={{
                                fontSize: child ? 13 : 14,
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                flex: 1
                                // fontWeight: (groupBy === "basket" && children?.length > 1) ? 600 : 500,
                            }}
                        >
                            {displayName}
                        </Typography>
                        {(!position.groupId.startsWith("default_") && (effectiveChildren?.length ?? 0) >= 1) ? (
                            <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', position: 'relative', zIndex: 2 }}>
                                {expanded ? <ThinArrowDownIcon sx={{ fontSize: '18px' }} /> : <ThinArrowRightIcon sx={{ fontSize: '18px' }} />}
                            </span>
                        ) : null}
                    </Stack>
                    
                    {/* Product Type, SL, TP Row - Positioned absolutely above */}
                    {!child && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: '100%',
                                marginBottom: '-16px',
                                left: 0,
                                right: 0,
                                top: '24px',
                                height: 0,
                                overflow: 'visible',
                                zIndex: 1,
                                pointerEvents: 'none',
                                opacity: position.quantity === 0 ? 0.5 : 1
                            }}
                        >
                            <Box sx={{ paddingLeft: '5px', position: 'relative' }}>
                                <Stack
                                    direction="row"
                                    columnGap={0.5}
                                    alignItems="center"
                                    sx={{
                                        overflow: 'visible',
                                        ml: '4px'
                                    }}
                                >
                                    {security?.categoryCode !== SecurityCategory_CategoryCode.STOCKS && <Typography
                                        variant="caption"
                                        sx={(theme) => ({
                                            fontSize: 12,
                                            lineHeight: 1,
                                            py: 0.25,
                                            borderRadius: 4,
                                            backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                            color: alpha(theme.palette.text.secondary, 0.7),
                                            textTransform: 'uppercase',
                                            flexShrink: 0
                                        })}
                                    >
                                        {exchangeToJSON(sec.exchange)}
                                    </Typography>}

                                    <Typography
                                        variant="caption"
                                        sx={(theme) => ({
                                            fontSize: '12px',
                                            lineHeight: 1,
                                            px: security?.categoryCode !== SecurityCategory_CategoryCode.STOCKS ? 0.25 : 0,
                                            py: 0.25,
                                            borderRadius: 4,
                                            backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                            color: alpha(theme.palette.text.secondary, 0.7),
                                            textTransform: 'uppercase',
                                            flexShrink: 0
                                        })}
                                    >
                                        {(() => {
                                            const product = productToJSON(getPositionProduct(position));
                                            return product === 'PRODUCT_UNKNOWN' ? '-' : product;
                                        })()}
                                    </Typography>
                                    {!child && (basketOpenSlOrder || basketOpenTargetOrder) && (
                                        <>
                                            {basketOpenSlOrder && (
                                                <Typography
                                                    variant="caption"
                                                    sx={(theme) => ({
                                                        fontSize: '12px',
                                                        lineHeight: 1,
                                                        px: 0.25,
                                                        py: 0.25,
                                                        borderRadius: 4,
                                                        backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                                        color: alpha(theme.palette.text.secondary, 0.7),
                                                        flexShrink: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    })}
                                                >
                                                    SL
                                                    <Box
                                                        component="span"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleSlTargetClick()
                                                        }}
                                                        sx={{
                                                            fontSize: '12px',
                                                            color: yellow,
                                                            cursor: 'pointer',
                                                            pointerEvents: 'auto',
                                                            padding: '2px 4px',
                                                            borderRadius: '3px',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                backgroundColor: alpha(yellow, 0.15),
                                                                boxShadow: `0 2px 4px ${alpha('#000', 0.2)}`
                                                            },
                                                            '&:active': {
                                                                boxShadow: 'none'
                                                            }
                                                        }}
                                                    >
                                                        {toLocaleStringIn(basketOpenSlOrder.triggerPrice / (security?.priceMultiplier ?? 100))}
                                                    </Box>
                                                </Typography>
                                            )}
                                            {basketOpenTargetOrder && (
                                                <Typography
                                                    variant="caption"
                                                    sx={(theme) => ({
                                                        fontSize: '12px',
                                                        lineHeight: 1,
                                                        px: 0.25,
                                                        py: 0.25,
                                                        borderRadius: 4,
                                                        backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                                        color: alpha(theme.palette.text.secondary, 0.7),
                                                        flexShrink: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    })}
                                                >
                                                    TP
                                                    <Box
                                                        component="span"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleSlTargetClick()
                                                        }}
                                                        className={classes.profitColor}
                                                        sx={(theme) => ({
                                                            fontSize: '12px',
                                                            cursor: 'pointer',
                                                            pointerEvents: 'auto',
                                                            padding: '2px 4px',
                                                            borderRadius: '3px',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                backgroundColor: alpha(theme.palette.success.main, 0.15),
                                                                boxShadow: `0 2px 4px ${alpha('#000', 0.2)}`
                                                            },
                                                            '&:active': {
                                                                boxShadow: 'none'
                                                            }
                                                        })}
                                                    >
                                                        {toLocaleStringIn(basketOpenTargetOrder.price / (security?.priceMultiplier ?? 100))}
                                                    </Box>
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                </Stack>
                            </Box>
                        </Box>
                    )}
                </TableCell>

                <TableCell className={cx(classes.tableCell, classes.qtyCell, !showGreeks ? quantityClass : null)} sx={{ position: 'relative' }}>
                    {expanded && borderColor && !child && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                right: 0,
                                height: '1px',
                                backgroundColor: borderColor
                            }}
                        />
                    )}
                    {showGreeks ? formatGreeks(positionGreeks?.delta ?? 0) : (
                        isIncomingPosition ? (
                            <>
                                <span style={{ opacity: 0.4, marginRight: '5px' }}>0</span>
                                <span className={cx(quantity > 0 ? classes.buyQuantity : undefined, quantity < 0 ? classes.sellQuantity : undefined)}>{quantity}</span>
                            </>
                        ) : quantity
                    )}
                </TableCell>
                <TableCell className={cx(classes.tableCell, classes.averageCell)} sx={{ position: 'relative' }}>
                    {expanded && borderColor && !child && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                right: 0,
                                height: '1px',
                                backgroundColor: borderColor
                            }}
                        />
                    )}
                    {showGreeks ? formatGreeks(positionGreeks?.theta ?? 0) : ((isIncomingPosition || incomingOrders.length > 0) ? '-' : (avgPrice ? toLocaleStringIn(avgPrice / (security?.priceMultiplier ?? 100)) : '-'))}
                </TableCell>
                <TableCell className={cx(classes.tableCell, classes.ltpCell)} sx={{ position: 'relative' }}>
                    {expanded && borderColor && !child && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                right: 0,
                                height: '1px',
                                backgroundColor: borderColor
                            }}
                        />
                    )}
                    {showGreeks ? formatGreeks(positionGreeks?.gamma ?? 0) : (incomingOrders.length > 0 || isIncomingPosition) ? '-' : <LtpCell position={position} />}
                </TableCell>
                <TableCell className={cx(classes.tableCell, classes.profitCell)} sx={{ position: 'relative' }}>
                    {expanded && borderColor && !child && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                right: 0,
                                height: '1px',
                                backgroundColor: borderColor
                            }}
                        />
                    )}
                    {showGreeks ? formatGreeks(positionGreeks?.vega ?? 0) : (isIncomingPosition ? '-' : (
                        <ProfitCell position={position} />
                    ))}
                    {!showGreeks && position.quantity !== 0 && !isIncomingPosition && !expanded && !child && (
                        <ActionMenu
                            position={position}
                            className="action-menu"
                            slTargetLabel={slTargetLabel}
                            onSlTargetClick={handleSlTargetClick}
                            isChildLeg={child}
                            standaloneConditionalExitEnabled={standaloneConditionalExitMenuEnabled}
                            standaloneConditionalExitLabel={standaloneConditionalExitMenuLabel}
                            onStandaloneConditionalExitClick={handleStandaloneConditionalExitFromMenu}
                            basketConditionalExitEnabled={basketConditionalExitMenuEnabled}
                            basketConditionalExitLabel={basketConditionalExitMenuLabel}
                            onBasketConditionalExitClick={handleBasketConditionalExitFromMenu}
                        />
                    )}
                </TableCell>
            </TableRow>
            {partialSquareOffLeg && (
                <PositionsTableRow
                    key={`${position.groupId}-${position.security.code}-squared`}
                    position={{
                        ...position,
                        quantity: 0,
                        avgPrice: partialSquareOffLeg.avgPrice,
                        realizedProfit: position.realizedProfit,
                        children: []
                    }}
                    expandEnabled={expandEnabled}
                    child
                    showGreeks={showGreeks}
                    openConditionalExitModal={openConditionalExitModal}
                    openBasketConditionalExitModal={openBasketConditionalExitModal}
                />
            )}

            {
                (expanded && (effectiveChildren?.length ?? 0) >= 1) ? <TableRow>
                    <TableCell className={cx(classes.tableCell, classes.symbolCell)} colSpan={5} sx={{ paddingY: '0px !important', position: 'relative', height: '5px' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: '5px',
                                height: '5px',
                                backgroundColor: borderColor,
                            }}
                        />
                    </TableCell>
                </TableRow> : null
            }

            {expanded && (effectiveChildren?.length ?? 0) >= 1 && effectiveChildren?.map((p, index) => (
                <PositionsTableRow
                    key={`${p.groupId}-${p.security.code}-${index}`}
                    position={{
                        ...p,
                        isFirstChild: index === 0,
                        isLastChild: index === (effectiveChildren?.length ?? 0) - 1 && position.quantity === 0
                    } as any}
                    expandEnabled={expandEnabled}
                    child
                    showGreeks={showGreeks}
                    openConditionalExitModal={openConditionalExitModal}
                    openBasketConditionalExitModal={openBasketConditionalExitModal}
                />
            ))}
            {expanded && incomingOrders?.length > 0 && incomingOrders.map((order, index) => {
                const isLastIncoming = index === incomingOrders.length - 1
                const hasActionRow = position.quantity !== 0
                return <PositionsTableRow
                    key={order.clientOrderId}
                    position={{
                        groupId: order.basketId,
                        quantity: order.transactionType === TransactionType.BUY ? order.quantity : -order.quantity,
                        avgPrice: order.price,
                        realizedProfit: 0,
                        security: order.security,
                        isIncoming: true,
                        originalPosition: {
                            product: order.product
                        },
                        isLastRow: isLastIncoming && !hasActionRow
                    } as any}
                    expandEnabled={expandEnabled}
                    child
                    showGreeks={showGreeks}
                    openConditionalExitModal={openConditionalExitModal}
                    openBasketConditionalExitModal={openBasketConditionalExitModal}
                />
            })}
            {expanded && (effectiveChildren?.length ?? 0) >= 1 && !position.groupId.startsWith("default_") && position.quantity !== 0 && (
                <>
                    <BasketExpandedProfitRow position={position} borderColor={borderColor} />

                    {/* Actions Row */}
                    <TableRow
                        sx={{
                            position: 'relative'
                        }}
                    >
                        <TableCell colSpan={5} className={cx(classes.tableCell)} sx={{ p: '0px !important', position: 'relative' }}>
                            {borderColor && (
                                <>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: '5px',
                                            backgroundColor: borderColor,
                                            borderBottomLeftRadius: '8px'
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: '5px',
                                            bottom: 0,
                                            right: 0,
                                            height: '1px',
                                            backgroundColor: borderColor
                                        }}
                                    />
                                </>
                            )}
                            <Stack 
                                ref={containerRef}
                                direction="row" 
                                spacing={0} 
                                sx={{ 
                                    width: '100%',
                                    flexWrap: shouldWrapButtons ? 'wrap' : 'nowrap',
                                    paddingBottom: '1px',
                                    '& > button': {
                                        flex: shouldWrapButtons ? '1 1 calc(50% - 0.5px)' : 1,
                                        minWidth: 0,
                                        borderRadius: 0,
                                        borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                                        borderBottom: shouldWrapButtons ? (theme) => `1px solid ${theme.palette.divider}` : 'none',
                                        fontSize: '12px',
                                        px: { xs: 0.5, sm: 1 },
                                        py: 0.5,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        '&:last-of-type': {
                                            borderRight: 'none',
                                        },
                                        '& svg': {
                                            fontSize: '18px',
                                            flexShrink: 0
                                        }
                                    }
                                }}
                            >
                                {(
                                    <ExtraSmallButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleSlTargetClick()
                                        }}
                                        title={slTargetLabel}
                                    >
                                        <PlusIcon fontSize="small" sx={{ mr: shouldWrapButtons ? 0 : 0.5 }} />
                                        <Box component="span" sx={{ display: shouldWrapButtons ? 'none' : 'inline' }}>
                                            {slTargetLabel}
                                        </Box>
                                    </ExtraSmallButton>
                                )}
                                {!basketExitHiddenByConditionalExit && (
                                <ExtraSmallButton
                                    size="small"
                                    title="Exit Position"
                                    onClick={async (e) => {
                                        e.stopPropagation()
                                        if (!position) return

                                        const isStandalone = position.children?.length === 1 && position.children[0].groupId?.startsWith('default')

                                        if (isStandalone) {
                                            const standalonePosition = position.children[0]
                                            const securityKey = getSecurityKey(standalonePosition.security?.exchange, standalonePosition.security?.code)
                                            const orderBooks = store.getState().securityStore.orderBooks
                                            const orderBookPrice = standalonePosition.quantity > 0 ? orderBooks[securityKey]?.bids[0]?.price : orderBooks[securityKey]?.asks[0]?.price

                                            const transactionType = standalonePosition.quantity > 0 ? TransactionType.SELL : TransactionType.BUY
                                            const quantity = Math.abs(standalonePosition.quantity)

                                            let finalProduct = standalonePosition.originalPosition?.product ?? Product.INTRADAY

                                            dispatch(
                                                setQuickOrderData({
                                                    openDialog: true,
                                                    mode: 'regular',
                                                    type: 'trade',
                                                    isSquareOffOrder: true,
                                                    maxQuantity: quantity,
                                                    order: {
                                                        transactionType,
                                                        security,
                                                        brokerOrderType: Broker_OrderType.MARKET_ORDER,
                                                        orderType: OrderType.NORMAL,
                                                        product: finalProduct,
                                                        price: orderBookPrice,
                                                        quantity
                                                    }
                                                })
                                            )
                                            return
                                        }
                                        const orders: TradeOrder[] = []
                                        const expiries = new Set<number>()

                                        let underlyingSecurity: Security | null = null
                                        for (const p of position.children || []) {
                                            if (!p.quantity) continue
                                            const sec = await getSecurityAsync(getSecurityKey(p.security.exchange, p.security.code))
                                            if (sec?.underlyingSecurityCode) {
                                                underlyingSecurity = await getSecurityAsync(getSecurityKey(sec.exchange, sec.underlyingSecurityCode))
                                            }
                                            if (sec?.expiry?.expiry) {
                                                expiries.add(sec.expiry.expiry)
                                            }
                                        }

                                        if (!underlyingSecurity) {
                                            underlyingSecurity = await getSecurityAsync(getSecurityKey(position?.originalPosition?.security?.exchange, position?.originalPosition?.security?.code))
                                        }

                                        if (!underlyingSecurity) return

                                        const payload: GetOptionChainRequestV2 = {
                                            security: { code: underlyingSecurity.code, exchange: underlyingSecurity.exchange },
                                            expiries: Array.from([...expiries]),
                                            noOfStrikes: -1,
                                            requestedFields: [],
                                            timeBucket: {
                                                timeSeries: TimeBucket_TimeSeries.DAY,
                                                value: 1
                                            },
                                            sendFutures: true
                                        }
                                        await fetchOptionChainV2(payload, dispatch, optionsTradesData, 'both', false)

                                        const orderBooks = store.getState().securityStore.orderBooks
                                        if (!position.children?.length) {
                                            const securityKey = getSecurityKey(position.security?.exchange, position.security?.code)
                                            const sec = await getSecurityAsync(securityKey)
                                            if (position.quantity !== 0 && sec) {
                                                orders.push(
                                                    getSquareOffOrder(
                                                        {
                                                            ...position,
                                                            security: sec
                                                        },
                                                        position.quantity > 0 ? orderBooks[securityKey]?.bids[0]?.price : orderBooks[securityKey]?.asks[0]?.price
                                                    )
                                                )
                                            }
                                        } else {
                                            for (const pos of position.children) {
                                                const securityKey = getSecurityKey(pos.security?.exchange, pos.security?.code)
                                                const sec = await getSecurityAsync(securityKey)
                                                if (pos.quantity !== 0 && sec) {
                                                    orders.push(
                                                        getSquareOffOrder(
                                                            {
                                                                ...pos,
                                                                security: sec
                                                            },
                                                            pos.quantity > 0 ? orderBooks[securityKey]?.bids[0]?.price : orderBooks[securityKey]?.asks[0]?.price
                                                        )
                                                    )
                                                }
                                            }
                                        }

                                        const batchId =
                                            resolveBatchIdForBasketFromOrders(orderHistory, position.groupId) ??
                                            uuid()

                                        if (underlyingSecurity) {
                                            const pendingState: StrategyBuilderLocationState = {
                                                [STRATEGY_BUILDER_LOCATION_PENDING_KEY]: {
                                                    kind: 'squareOff',
                                                    batchId,
                                                    basketId: position.groupId,
                                                    strategyGroup: position.groupName,
                                                    orders
                                                }
                                            }
                                            navigate(
                                                generatePath(ROUTES.strategyBuilder, {
                                                    exchange: exchangeToJSON(underlyingSecurity.exchange),
                                                    symbol: underlyingSecurity.code
                                                }),
                                                { state: pendingState }
                                            )
                                        }
                                    }}
                                >
                                    <ExitIcon fontSize="small" sx={{ mr: shouldWrapButtons ? 0 : 0.5 }} />
                                    <Box component="span" sx={{ display: shouldWrapButtons ? 'none' : 'inline' }}>
                                        Exit
                                    </Box>
                                </ExtraSmallButton>
                                )}
                                {position.children?.length > 1 && (
                                    <ExtraSmallButton
                                        size="small"
                                        title="Adjust/Analyze Position"
                                        onClick={async (e) => {
                                            e.stopPropagation()
                                            if (!position) return

                                            const expiries = new Set<number>()
                                            let underlyingSecurity: Security | null = null
                                            for (const p of position.children || []) {
                                                const sec = await getSecurityAsync(getSecurityKey(p.security.exchange, p.security.code))
                                                if (sec?.underlyingSecurityCode) {
                                                    underlyingSecurity = await getSecurityAsync(getSecurityKey(sec.exchange, sec.underlyingSecurityCode))
                                                }
                                                if (sec?.expiry?.expiry) {
                                                    expiries.add(sec.expiry.expiry)
                                                }
                                            }

                                            if (!underlyingSecurity) {
                                                underlyingSecurity = await getSecurityAsync(getSecurityKey(position?.originalPosition?.security?.exchange, position?.originalPosition?.security?.code))
                                            }

                                            if (!underlyingSecurity) return

                                            const payload: GetOptionChainRequestV2 = {
                                                security: { code: underlyingSecurity.code, exchange: underlyingSecurity.exchange },
                                                expiries: Array.from([...expiries]),
                                                noOfStrikes: -1,
                                                requestedFields: [],
                                                timeBucket: {
                                                    timeSeries: TimeBucket_TimeSeries.DAY,
                                                    value: 1
                                                },
                                                sendFutures: true
                                            }
                                            await fetchOptionChainV2(payload, dispatch, optionsTradesData, 'both', false)

                                            if (underlyingSecurity) {
                                                const analyzeBatchId =
                                                    resolveBatchIdForAnalyzeFromOrders(
                                                        orderHistory,
                                                        position.groupId
                                                    ) ??
                                                    resolveBatchIdForBasketFromOrders(
                                                        orderHistory,
                                                        position.groupId
                                                    ) ??
                                                    uuid()
                                                const pendingState: StrategyBuilderLocationState = {
                                                    [STRATEGY_BUILDER_LOCATION_PENDING_KEY]: {
                                                        kind: 'analyze',
                                                        batchId: analyzeBatchId,
                                                        basketId: position.groupId,
                                                        strategyGroup: position.groupName
                                                    }
                                                }
                                                navigate(
                                                    generatePath(ROUTES.strategyBuilder, {
                                                        exchange: exchangeToJSON(underlyingSecurity.exchange),
                                                        symbol: underlyingSecurity.code
                                                    }),
                                                    { state: pendingState }
                                                )
                                            }
                                        }}
                                    >
                                        <TuneOutlinedIcon sx={{ fontSize: '20px', mr: shouldWrapButtons ? 0 : 0.5 }} />
                                        <Box component="span" sx={{ display: shouldWrapButtons ? 'none' : 'inline' }}>
                                            {containerWidth > 500 ? 'Adjust/Analyze' : 'Adjust'}
                                        </Box>
                                    </ExtraSmallButton>
                                )}
                                <ExtraSmallButton
                                    size="small"
                                    title="View Chart"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (!position) return

                                        if (position.children?.length == 1) {
                                            window.open(origin + `${generatePath(ROUTES.symbol, { symbol: getSecurityKey(position.security.exchange, position.security.code) })}`, '_blank')
                                        } else {
                                            window.open(`/strategy/${position.groupId}`, '_blank')
                                        }
                                    }}
                                >
                                    <Chart sx={{ height: '25px', width: '25px', mr: shouldWrapButtons ? 0 : 0.5 }} strokeWidth={1} />
                                    <Box component="span" sx={{ display: shouldWrapButtons ? 'none' : 'inline' }}>
                                        Chart
                                    </Box>
                                </ExtraSmallButton>
                            </Stack>
                        </TableCell>
                    </TableRow>
                </>
            )}

            {openSlTarget ? <SlTargetDialog position={position} open={openSlTarget} onClose={() => setOpenSlTarget(false)} /> : null}
        </>
    )
}
const arePositionsTableRowPropsEqual = (
    prev: PositionsTableRowProps,
    next: PositionsTableRowProps
): boolean => {
    if (
        prev.expandEnabled !== next.expandEnabled ||
        prev.showGreeks !== next.showGreeks ||
        prev.child !== next.child ||
        prev.openConditionalExitModal !== next.openConditionalExitModal ||
        prev.openBasketConditionalExitModal !== next.openBasketConditionalExitModal
    ) {
        return false
    }
    const a = prev.position
    const b = next.position
    return (
        a.groupId === b.groupId &&
        a.quantity === b.quantity &&
        a.avgPrice === b.avgPrice &&
        a.realizedProfit === b.realizedProfit &&
        (a.children?.length ?? 0) === (b.children?.length ?? 0)
    )
}

export default memo(PositionsTableRow, arePositionsTableRowPropsEqual)
